import React, { useState, useEffect, useCallback, useMemo } from 'react'
import axios from 'axios'
import { Modal, message, Skeleton } from 'antd'
import { SaveOutlined, DownloadOutlined } from '@ant-design/icons'

// ─── Constantes ───────────────────────────────────────────────────────────────

const MESES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic']

const C = {
  headerBg:  '#1A3A5C',
  headerTxt: '#FFFFFF',
  rowOdd:    '#F8FAFC',
  rowEven:   '#FFFFFF',
  hover:     '#EBF4FF',
  dirty:     '#FFFDE7',
  totalBg:   '#E8F5E9',
  totalTxt:  '#1B5E20',
  groupBg:   '#EEF2F8',
  acumAntBg: '#F5F5F5',
  border:    '#E0E0E0',
  stickyBg:  '#FAFBFC',
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) =>
  (typeof n === 'number' ? n : parseFloat(n) || 0)
    .toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fmtFecha = (iso) => {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    const p = (v) => String(v).padStart(2, '0')
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
  } catch { return '—' }
}

const cellKey = (tipoId, mes) => `${tipoId}_${mes}`

// ─── Estilos de tabla ─────────────────────────────────────────────────────────

const TH = {
  background: C.headerBg,
  color: C.headerTxt,
  padding: '6px 8px',
  fontSize: 11,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  textAlign: 'center',
  borderBottom: `1px solid ${C.border}`,
  borderRight: `1px solid rgba(255,255,255,0.1)`,
  userSelect: 'none',
  position: 'sticky',
  top: 0,
  zIndex: 2,
}

const TD = {
  padding: '3px 8px',
  fontSize: 12,
  verticalAlign: 'middle',
  borderBottom: `1px solid ${C.border}`,
  borderRight: `1px solid ${C.border}`,
}

const TD_NUM = { ...TD, textAlign: 'right', fontFamily: 'monospace', minWidth: 75 }

// ─── Celda editable ───────────────────────────────────────────────────────────

function CeldaEditable({ valor, readOnly, onCommit }) {
  const [local, setLocal] = useState(String(valor ?? 0))

  useEffect(() => { setLocal(String(valor ?? 0)) }, [valor])

  if (readOnly) {
    return <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{fmt(valor)}</span>
  }

  const commit = () => {
    const v = parseFloat(local)
    const efectivo = isNaN(v) ? 0 : Math.round(v * 100) / 100
    setLocal(String(efectivo))
    onCommit(efectivo)
  }

  return (
    <input
      type="number"
      step="0.01"
      value={local}
      className="ma-input"
      style={{
        width: '100%',
        border: 'none',
        background: 'transparent',
        textAlign: 'right',
        fontFamily: 'monospace',
        fontSize: 12,
        outline: 'none',
        cursor: 'text',
        padding: '0 2px',
        MozAppearance: 'textfield',
        WebkitAppearance: 'none',
      }}
      onFocus={(e) => e.target.select()}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          commit()
          const inputs = document.querySelectorAll('.ma-input')
          const idx = Array.from(inputs).indexOf(e.target)
          if (idx >= 0 && idx < inputs.length - 1) inputs[idx + 1].focus()
        }
      }}
    />
  )
}

// ─── Esqueleto de carga ───────────────────────────────────────────────────────

function SkeletonRows() {
  return Array.from({ length: 8 }, (_, i) => (
    <tr key={i}>
      {Array.from({ length: 16 }, (_, j) => (
        <td key={j} style={{ ...TD, padding: '7px 8px' }}>
          <Skeleton.Input active size="small" style={{ width: j === 0 ? 180 : 60, height: 14 }} />
        </td>
      ))}
    </tr>
  ))
}

// ─── Modal secundario: Importar periodo ──────────────────────────────────────

const MESES_DESTINO = [
  { val: 0,  label: 'Importe inicial del ejercicio' },
  { val: 1,  label: 'Enero' },   { val: 2,  label: 'Febrero' },
  { val: 3,  label: 'Marzo' },   { val: 4,  label: 'Abril' },
  { val: 5,  label: 'Mayo' },    { val: 6,  label: 'Junio' },
  { val: 7,  label: 'Julio' },   { val: 8,  label: 'Agosto' },
  { val: 9,  label: 'Septiembre' }, { val: 10, label: 'Octubre' },
  { val: 11, label: 'Noviembre' },  { val: 12, label: 'Diciembre' },
]

function ModalImportarPeriodo({ open, onClose, tiposAcumulados, onAplicar }) {
  const [mesDestino, setMesDestino] = useState(0)
  const [valores, setValores] = useState({})

  useEffect(() => {
    if (open) { setValores({}); setMesDestino(0) }
  }, [open])

  const handleAplicar = () => {
    const items = tiposAcumulados
      .map(t => ({ tipoId: t._id?.toString(), mes: mesDestino, valor: parseFloat(valores[t._id?.toString()]) || 0 }))
      .filter(({ valor }) => valor !== 0)
    onAplicar(items)
    onClose()
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Importar acumulados de otro sistema"
      footer={null}
      width={560}
      centered
    >
      {/* Selector mes destino */}
      <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 12, color: '#555', fontWeight: 500 }}>Mes destino:</span>
        <select
          value={mesDestino}
          onChange={(e) => setMesDestino(Number(e.target.value))}
          style={{ border: '1px solid #d0d0d0', borderRadius: 4, padding: '4px 8px', fontSize: 12, background: '#fff', cursor: 'pointer' }}
        >
          {MESES_DESTINO.map(({ val, label }) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
      </div>

      {/* Tabla de tipos */}
      <div style={{ maxHeight: 400, overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: 4 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ ...TH, textAlign: 'left', padding: '6px 10px' }}>Tipo de acumulado</th>
              <th style={{ ...TH, width: 140 }}>Importe a importar</th>
            </tr>
          </thead>
          <tbody>
            {tiposAcumulados.map((tipo, idx) => {
              const id = String(tipo._id)
              return (
                <tr key={id} style={{ background: idx % 2 === 0 ? C.rowOdd : C.rowEven }}>
                  <td style={{ ...TD, padding: '4px 10px' }}>
                    {tipo.numero != null && (
                      <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#bbb', marginRight: 6 }}>
                        {tipo.numero}
                      </span>
                    )}
                    {tipo.nombre}
                  </td>
                  <td style={{ ...TD_NUM, padding: '2px 8px' }}>
                    <input
                      type="number"
                      step="0.01"
                      value={valores[id] ?? ''}
                      placeholder="0.00"
                      style={{
                        width: '100%', border: 'none', background: 'transparent',
                        textAlign: 'right', fontFamily: 'monospace', fontSize: 12,
                        outline: 'none', padding: '0 2px',
                      }}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => setValores(p => ({ ...p, [id]: e.target.value }))}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Nota informativa */}
      <div style={{ fontSize: 11, color: '#9E9E9E', marginTop: 10 }}>
        Los valores se aplicarán como cambios pendientes. Recuerda guardar antes de cerrar.
      </div>

      {/* Botones */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button
          onClick={onClose}
          style={{ padding: '5px 14px', borderRadius: 4, border: '1px solid #9E9E9E', background: '#fff', cursor: 'pointer', fontSize: 12 }}
        >
          Cancelar
        </button>
        <button
          onClick={handleAplicar}
          style={{ padding: '5px 14px', borderRadius: 4, border: `1px solid ${C.headerBg}`, background: C.headerBg, color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 500 }}
        >
          Aplicar importación
        </button>
      </div>
    </Modal>
  )
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function ModalAcumulados({
  empleadoId,
  empresaId,
  anio: anioProp = new Date().getFullYear(),
  open,
  onClose,
  readOnly = false,
  headersToken = {},
}) {
  const [anio, setAnio] = useState(anioProp)
  const [tiposAcumulados, setTiposAcumulados] = useState([])
  const [acumulados, setAcumulados] = useState({})
  const [importesIniciales, setImportesIniciales] = useState({})
  const [acumAnteriores, setAcumAnteriores] = useState({})
  const [dirty, setDirty] = useState(new Set())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [ultimaMod, setUltimaMod] = useState(null)
  const [nombreEmpleado, setNombreEmpleado] = useState('')
  const [hoveredRow, setHoveredRow] = useState(null)
  const [modalImportar, setModalImportar] = useState(false)
  const [reintento, setReintento] = useState(0)

  useEffect(() => { setAnio(anioProp) }, [anioProp])

  useEffect(() => {
    console.log('[MA] useEffect disparado — open:', open, '| empleadoId:', empleadoId, '| empresaId:', empresaId, '| anio:', anio, '| reintento:', reintento)
    if (!open || !empleadoId || !empresaId) {
      console.log('[MA] guard activado — no se carga. open:', open, 'empleadoId:', empleadoId, 'empresaId:', empresaId)
      return
    }

    const cargarDatos = async () => {
      console.log('[MA] cargarDatos iniciado')
      setLoading(true)
      setError(null)
      try {
        console.log('[MA] llamando API con:', { empleadoId, empresaId, anio })
        const [resConAcum, resAnt] = await Promise.all([
          axios.post('acumulados/listarConAcumulados',
            { empleadoId, empresaId, anio },
            { headers: headersToken }
          ),
          axios.post('acumulados/listarConAcumulados',
            { empleadoId, empresaId, anio: anio - 1 },
            { headers: headersToken }
          ),
        ])

        console.log('[MA] resConAcum.data.success:', resConAcum.data.success, '| data.length:', resConAcum.data.data?.length)
        console.log('[MA] resAnt.data.success:', resAnt.data.success)

        if (resConAcum.data.success === 2) {
          const filas = resConAcum.data.data || []
          console.log('[MA] filas recibidas:', filas.length, '| primera fila:', filas[0])

          setTiposAcumulados(filas.map(f => ({
            _id:           String(f.tipoAcumuladoId),
            numero:        f.numero,
            nombre:        f.nombre,
            tipoAcumulado: f.tipoAcumulado,
            serie:         f.serie,
          })))

          const newIni = {}
          filas.forEach(f => {
            newIni[String(f.tipoAcumuladoId)] = f.meses[0] ?? 0
          })
          setImportesIniciales(newIni)
          console.log('[MA] newIni:', newIni)

          const newAcum = {}
          filas.forEach(f => {
            const id = String(f.tipoAcumuladoId)
            for (let m = 1; m <= 12; m++) {
              newAcum[`${id}_${m}`] = f.meses[m] ?? 0
            }
          })
          setAcumulados(newAcum)
          console.log('[MA] newAcum (primeras 3 keys):', Object.keys(newAcum).slice(0, 3))

          if (resConAcum.data.empleado) {
            const e = resConAcum.data.empleado
            setNombreEmpleado(
              `${e.nombre || ''} ${e.apellidoPaterno || ''} ${e.apellidoMaterno || ''}`.trim()
            )
          }
        } else {
          console.warn('[MA] resConAcum no tuvo success=2:', resConAcum.data)
        }

        if (resAnt.data.success === 2) {
          const tots = {}
          ;(resAnt.data.data || []).forEach(f => {
            tots[String(f.tipoAcumuladoId)] = f.meses.slice(1).reduce((s, v) => s + (v || 0), 0)
          })
          setAcumAnteriores(tots)
        }

        setDirty(new Set())
        console.log('[MA] carga completada OK')
      } catch (err) {
        console.error('[MA] Error en carga:', err)
        setError('Error al cargar acumulados.')
      }
      setLoading(false)
    }

    cargarDatos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, empleadoId, empresaId, anio, reintento])

  const handleCellChange = useCallback((tipoId, mes, valor) => {
    const key = cellKey(tipoId, mes)
    if (mes === 0) {
      setImportesIniciales((p) => ({ ...p, [tipoId]: valor }))
    } else {
      setAcumulados((p) => ({ ...p, [key]: valor }))
    }
    setDirty((p) => new Set([...p, key]))
  }, [])

  const handleSave = useCallback(async () => {
    if (readOnly || dirty.size === 0) return
    setSaving(true)
    const registros = []
    tiposAcumulados.forEach((tipo) => {
      const id = tipo._id?.toString()
      if (!id) return
      const iniKey = cellKey(id, 0)
      if (dirty.has(iniKey)) {
        registros.push({ tipoAcumuladoId: id, mes: 0, acumulado: importesIniciales[id] ?? 0 })
      }
      for (let m = 1; m <= 12; m++) {
        const k = cellKey(id, m)
        if (dirty.has(k)) {
          registros.push({ tipoAcumuladoId: id, mes: m, acumulado: acumulados[k] ?? 0 })
        }
      }
    })
    try {
      const res = await axios.post(
        'acumulados/upsertBatch',
        { empleadoId, empresaId, anio, registros },
        { headers: headersToken }
      )
      if (res.data.success === 2) {
        message.success('Acumulados guardados correctamente')
        setDirty(new Set())
        setUltimaMod(new Date().toISOString())
      } else {
        message.error(res.data.message || 'Error al guardar acumulados')
      }
    } catch {
      message.error('Error de conexión al guardar')
    }
    setSaving(false)
  }, [readOnly, tiposAcumulados, dirty, importesIniciales, acumulados, empleadoId, empresaId, anio, headersToken])

  const handleClose = useCallback(() => {
    if (dirty.size > 0) {
      Modal.confirm({
        title: '¿Descartar cambios?',
        content: 'Hay cambios sin guardar. Si cierra ahora, se perderán.',
        okText: 'Descartar',
        cancelText: 'Continuar editando',
        okType: 'danger',
        onOk: () => { setDirty(new Set()); onClose() },
      })
    } else {
      onClose()
    }
  }, [dirty, onClose])

  const handleExport = useCallback(() => {
    const headers = ['Nombre', 'Acum. Ej. Anterior', 'Importe Inicial', ...MESES, 'Total']
    const rows = tiposAcumulados.map((tipo) => {
      const id = tipo._id?.toString()
      const ant = acumAnteriores[id] || 0
      const ini = importesIniciales[id] || 0
      const mesesVals = Array.from({ length: 12 }, (_, i) => acumulados[cellKey(id, i + 1)] || 0)
      const total = ini + mesesVals.reduce((s, v) => s + v, 0)
      return [`"${tipo.nombre}"`, ant, ini, ...mesesVals, total].join(',')
    })
    const csv = [headers.join(','), ...rows].join('\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `acumulados_${anio}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }, [tiposAcumulados, acumAnteriores, importesIniciales, acumulados, anio])

  const handleAplicarImportacion = useCallback((items) => {
    items.forEach(({ tipoId, mes, valor }) => handleCellChange(tipoId, mes, valor))
  }, [handleCellChange])

  const calcTotal = useCallback(
    (tipoId) => {
      const ini = importesIniciales[tipoId] || 0
      return (
        ini +
        Array.from({ length: 12 }, (_, i) => acumulados[cellKey(tipoId, i + 1)] || 0).reduce(
          (s, v) => s + v,
          0
        )
      )
    },
    [importesIniciales, acumulados]
  )

  const grupos = useMemo(() => {
    const g1 = tiposAcumulados.filter((t) => t.tipoAcumulado !== 2)
    const g2 = tiposAcumulados.filter((t) => t.tipoAcumulado === 2)
    const result = []
    if (g1.length) result.push({ key: 1, label: 'Percepciones / Deducciones / Obligaciones', tipos: g1 })
    if (g2.length) result.push({ key: 2, label: 'Días y Horas', tipos: g2 })
    return result
  }, [tiposAcumulados])

  const aniosDisponibles = useMemo(
    () => [anioProp, anioProp - 1, anioProp - 2],
    [anioProp]
  )

  // ── Render ──────────────────────────────────────────────────────────────────

  const tableRows = grupos.flatMap((grupo, gi) => {
    const groupRowTopOffset = 33 // px — height of the sticky TH row
    return [
      <tr key={`g-${grupo.key}`}>
        <td
          colSpan={16}
          style={{
            background: C.groupBg,
            padding: '4px 14px',
            fontSize: 11,
            fontWeight: 700,
            color: C.headerBg,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            borderBottom: `1px solid ${C.border}`,
            position: 'sticky',
            top: groupRowTopOffset,
            zIndex: 1,
          }}
        >
          {grupo.label}
        </td>
      </tr>,
      ...grupo.tipos.map((tipo, idx) => {
        const id = String(tipo._id)
        const isOdd = idx % 2 === 0
        const isHov = hoveredRow === id
        const rowBg = isHov ? C.hover : isOdd ? C.rowOdd : C.rowEven
        const iniKey = cellKey(id, 0)

        return (
          <tr key={id} onMouseEnter={() => setHoveredRow(id)} onMouseLeave={() => setHoveredRow(null)}>
            {/* Nombre — sticky */}
            <td
              style={{
                ...TD,
                background: isHov ? C.hover : C.stickyBg,
                position: 'sticky',
                left: 0,
                zIndex: 1,
                borderRight: `2px solid ${C.border}`,
                minWidth: 220,
                maxWidth: 300,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {tipo.numero != null && (
                <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#bbb', marginRight: 6 }}>
                  {tipo.numero}
                </span>
              )}
              {tipo.nombre}
            </td>

            {/* Acum. Ejercicio Anterior */}
            <td style={{ ...TD_NUM, background: C.acumAntBg, color: '#666' }}>
              {fmt(acumAnteriores[id] || 0)}
            </td>

            {/* Importe Inicial Ejercicio */}
            <td style={{ ...TD_NUM, background: dirty.has(iniKey) ? C.dirty : rowBg }}>
              <CeldaEditable
                valor={importesIniciales[id] ?? 0}
                readOnly={readOnly}
                onCommit={(v) => handleCellChange(id, 0, v)}
              />
            </td>

            {/* Meses 1–12 */}
            {Array.from({ length: 12 }, (_, i) => {
              const mes = i + 1
              const k = cellKey(id, mes)
              return (
                <td key={mes} style={{ ...TD_NUM, background: dirty.has(k) ? C.dirty : rowBg }}>
                  <CeldaEditable
                    valor={acumulados[k] ?? 0}
                    readOnly={readOnly}
                    onCommit={(v) => handleCellChange(id, mes, v)}
                  />
                </td>
              )
            })}

            {/* Total */}
            <td style={{ ...TD_NUM, background: C.totalBg, fontWeight: 700, color: C.totalTxt }}>
              {fmt(calcTotal(id))}
            </td>
          </tr>
        )
      }),
    ]
  })

  const canSave = !readOnly && !saving && dirty.size > 0

  console.log('[MA RENDER] loading:', loading, '| tiposAcumulados.length:', tiposAcumulados.length, '| grupos.length:', grupos.length, '| tableRows.length:', tableRows.length, '| open:', open)

  const btnBase = {
    padding: '5px 14px',
    borderRadius: 4,
    border: '1px solid',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    lineHeight: 1.4,
  }

  return (
    <Modal
      visible={open}
      onCancel={handleClose}
      width="90vw"
      style={{ maxWidth: 1400 }}
      bodyStyle={{ padding: 0, height: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      title={null}
      footer={null}
      destroyOnClose
      centered
      closable={false}
    >
      {/* Suprimir spinners de input[type=number] */}
      <style>{`
        .ma-input::-webkit-inner-spin-button,
        .ma-input::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        .ma-input[type=number] { -moz-appearance: textfield; }
      `}</style>

      {/* ── Header ── */}
      <div
        style={{
          padding: '12px 20px',
          background: C.headerBg,
          color: C.headerTxt,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          borderRadius: '8px 8px 0 0',
        }}
      >
        <div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>
            Acumulados del Ejercicio{nombreEmpleado ? ` – ${nombreEmpleado}` : ''}
          </div>
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
            Ejercicio {anio}
            {readOnly && (
              <span style={{ background: '#E53935', borderRadius: 3, padding: '1px 6px', fontSize: 10, opacity: 1 }}>
                Solo lectura
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <select
            value={anio}
            onChange={(e) => setAnio(Number(e.target.value))}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: C.headerTxt,
              borderRadius: 4,
              padding: '4px 8px',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            {aniosDisponibles.map((y) => (
              <option key={y} value={y} style={{ background: C.headerBg, color: '#fff' }}>
                {y}
              </option>
            ))}
          </select>
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: C.headerTxt,
              fontSize: 20,
              cursor: 'pointer',
              padding: '0 2px',
              lineHeight: 1,
              opacity: 0.8,
            }}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div
          style={{
            background: '#FFF3F3',
            borderBottom: '1px solid #FFCDD2',
            padding: '8px 20px',
            fontSize: 12,
            color: '#C62828',
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {error}
          <button
            onClick={() => setReintento(r => r + 1)}
            style={{ background: 'none', border: '1px solid #C62828', color: '#C62828', borderRadius: 3, padding: '2px 10px', cursor: 'pointer', fontSize: 12 }}
          >
            Reintentar
          </button>
        </div>
      )}

      {/* ── Tabla ── */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'auto' }}>
          <thead>
            <tr>
              <th
                style={{
                  ...TH,
                  textAlign: 'left',
                  minWidth: 220,
                  position: 'sticky',
                  left: 0,
                  top: 0,
                  zIndex: 4,
                  borderRight: `2px solid rgba(255,255,255,0.2)`,
                }}
              >
                Nombre
              </th>
              <th style={TH}>Acum. Ej. Anterior</th>
              <th style={TH}>Importe Inicial Ejercicio</th>
              {MESES.map((m) => (
                <th key={m} style={TH}>{m}</th>
              ))}
              <th style={{ ...TH, background: '#1B5E20', minWidth: 90, zIndex: TH.zIndex }}>
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? <SkeletonRows />
              : tiposAcumulados.length === 0
                ? (
                  <tr>
                    <td
                      colSpan={16}
                      style={{
                        textAlign: 'center',
                        padding: '40px 0',
                        color: '#999',
                        fontSize: 13,
                        fontStyle: 'italic',
                      }}
                    >
                      No hay tipos de acumulado configurados para esta empresa.
                    </td>
                  </tr>
                )
                : tableRows
            }
          </tbody>
        </table>
      </div>

      {/* ── Footer ── */}
      <div
        style={{
          padding: '10px 20px',
          background: '#F8FAFC',
          borderTop: `1px solid ${C.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <div style={{ fontSize: 11, color: '#888' }}>
          Última modificación: {fmtFecha(ultimaMod)}
          {dirty.size > 0 && (
            <span style={{ marginLeft: 10, color: '#F57F17', fontWeight: 600 }}>
              {dirty.size} celda{dirty.size !== 1 ? 's' : ''} sin guardar
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setModalImportar(true)}
            disabled={readOnly}
            style={{ ...btnBase, background: '#fff', borderColor: '#9E9E9E', color: readOnly ? '#bbb' : '#555', cursor: readOnly ? 'not-allowed' : 'pointer' }}
          >
            Importar periodo
          </button>
          <button
            onClick={handleExport}
            style={{ ...btnBase, background: '#fff', borderColor: '#9E9E9E', color: '#555' }}
          >
            <DownloadOutlined style={{ fontSize: 13 }} /> Exportar
          </button>
          <button
            onClick={handleClose}
            style={{ ...btnBase, background: '#fff', borderColor: '#9E9E9E', color: '#555' }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            style={{
              ...btnBase,
              background: canSave ? C.headerBg : '#E0E0E0',
              borderColor: canSave ? C.headerBg : '#BDBDBD',
              color: canSave ? '#fff' : '#9E9E9E',
              cursor: canSave ? 'pointer' : 'not-allowed',
              opacity: saving ? 0.7 : 1,
            }}
          >
            <SaveOutlined style={{ fontSize: 13 }} />
            {saving ? 'Guardando…' : 'Guardar cambios'}
            {canSave && (
              <span
                style={{
                  background: '#FF5722',
                  color: '#fff',
                  borderRadius: '50%',
                  fontSize: 9,
                  width: 16,
                  height: 16,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {dirty.size}
              </span>
            )}
          </button>
        </div>
      </div>

      <ModalImportarPeriodo
        open={modalImportar}
        onClose={() => setModalImportar(false)}
        tiposAcumulados={tiposAcumulados}
        onAplicar={handleAplicarImportacion}
      />
    </Modal>
  )
}
